import { useState, useEffect, useMemo, useContext } from 'react';
import styles from '../Applicants/css/display-applicant.module.css';
import { useQuery } from '@tanstack/react-query';
import { FilterDropDown } from '../Applicants/Applicants'; 
import { AiOutlineSearch as SearchIcon } from 'react-icons/ai';
import { IoReload as ReloadIcon } from 'react-icons/io5';
import apiCall from '../../../utils/apiCall';
import { CircularProgress, styled } from '@mui/material';
import { AxiosError } from 'axios';
import { ExNyRoute, PrRoute, ExRoute, AcRoute, MiRoute, CoRoute } from '../../../utils/allRouteId';
import { RouteResponse } from '../../../types/index';
import { FetchAllRoutes, reOrderFetchedData } from '../../../hooks/FetchData';
import { Button, DateFilter } from '../../../ui-components';
import { NotificationContext } from '../../../providers/notification';
import { apiRequest, config } from '../../../api';
import fileDownload from 'js-file-download';

interface Applicants {
  ApplicantId: string;
  Name: string;
  Email: string;
  Phone: string;
  Academic: string;
  Academic_Score: string;
  Hr_Experience: string;
  Hr_Experience_Score: string;
  Sector: string;
  Trainings: string;
  Trainings_Score: string;
  Present_Employer: string;
  Designation: string;
  Total_Score: string;
  Year_experience: string;
  Remarks: string;
  Designation_Score: string;

}

interface DisplayApplicantsProps {
  setRouteName: React.Dispatch<React.SetStateAction<string>>;
}

const option1:any= [
    // {val:1, name:"Memembership Route",path:`${BASE_URL}`},
     {val: ExNyRoute, name:"Examination / NYSC"},
    {val:PrRoute, name:"Practitioner"},
    {val:ExRoute, name:"Executive"},
    {val: AcRoute, name:"Academic"},
    {val: MiRoute, name:"Military"},
    {val:CoRoute, name:"Corporate"}
  ]

const statusOptions = [
  { val: 1, name: "Pending Approval" },
  { val: 2, name: "Approved" }
];

const DisplayApplicant = ({ setRouteName }: DisplayApplicantsProps) => {
  const { showNotification } = useContext(NotificationContext);

  const [filters, setFilters] = useState({
    routeId: Number(ExRoute),
    searchData: '',
    date: { start: '', end: '' },
  });

  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 50;

  const [d_loading, set_d_loading] = useState(false);
  const [routeNameInt, setRouteNameInt] = useState('');

  const formatDate = (date: string) => {
    if (!date) return '';
    const [year, month, day] = date.split('-');
    return `${year}-${month}-${day}`;
  };

  /* ================= FETCH APPLICANTS ================= */
  const getAllApplicants = async () => {
    const params = new URLSearchParams();

    params.append('PageNumber', currentPage.toString());
    params.append('PageSize', pageSize.toString());
    params.append('RouteId', filters.routeId.toString());

    if (filters.searchData.trim()) {
      params.append('searchQuery', filters.searchData.trim());
    }

    if (filters.date.start) {
      params.append('startDate', formatDate(filters.date.start));
    }

    if (filters.date.end) {
      params.append('endDate', formatDate(filters.date.end));
    }

    const response = await apiCall().get(
      `/Applicant/GetAllApplicantDetailByRouteId?${params.toString()}`
    );

    return response.data;
  };

  const getAllApplicantsQuery = useQuery(
    [
      'get-All-Applicant',
      filters.routeId,
      filters.searchData,
      filters.date.start,
      filters.date.end,
      currentPage,
    ],
    getAllApplicants,
    {
      retry: 0,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
    }
  );

  const apiData = getAllApplicantsQuery.data;
  const applicants = apiData?.Data ?? [];
  const totalCount = apiData?.TotalSize ?? 0;

  const pageCount = Math.ceil(totalCount / pageSize);

  const startRecord =
    totalCount === 0 ? 0 : (currentPage - 1) * pageSize + 1;

  const endRecord = Math.min(currentPage * pageSize, totalCount);

  /* ================= ROUTES ================= */
  const { isLoading, data } = FetchAllRoutes();
  const reOrderedAllRoutes = data
    ? reOrderFetchedData((data as RouteResponse)?.Data)
    : [];

  const allRoutes = isLoading
    ? []
    : reOrderedAllRoutes.map(item => ({
        name: item.RouteName,
        val: item.RouteId,
      }));

  const switchRouteId = (route: { val: number; name: string }) => {
    setFilters(prev => ({ ...prev, routeId: route.val }));
    setRouteName(route.name);
    setRouteNameInt(route.name);
    setCurrentPage(1);
  };

  /* ================= DOWNLOAD ================= */
  const handleDownload = () => {
    set_d_loading(true);

    const params = new URLSearchParams();
    params.append('routeId', filters.routeId.toString());

    if (filters.date.start) {
      params.append('startDate', formatDate(filters.date.start));
    }
    if (filters.date.end) {
      params.append('endDate', formatDate(filters.date.end));
    }

    apiRequest(
      `api/Applicant/DownloadAllApplicantDetailByRouteId?${params.toString()}`,
      'GET',
      {},
      config,
      true
    )
      .then((data: any) => {
        if (!data?.Message) {
          fileDownload(data, `applicants-${Date.now()}.xlsx`);
          showNotification({
            message: 'File downloading...',
            type: 'success',
          });
        } else {
          showNotification({
            message: data.Message,
            type: 'info',
          });
        }
      })
      .catch(() => {
        showNotification({
          message: 'Sorry, file cannot be downloaded',
          type: 'error',
        });
      })
      .finally(() => set_d_loading(false));
  };

  return (
    <div className={styles.container}>
      <div className={styles.filterDiv}>
        <label className={styles.searchForm}>
          <span className={styles.sI}><SearchIcon /></span>
          <input
            type="text"
            placeholder="Search"
            value={filters.searchData}
            onChange={e => {
              setFilters(prev => ({ ...prev, searchData: e.target.value }));
              setCurrentPage(1);
            }}
          />
        </label>

        <FilterDropDown
          onChange={switchRouteId}
          text={routeNameInt || 'Membership Route'}
          bg="#0A3922"
          color="#fff"
          options={allRoutes}
        />

        <DateFilter
          onChange={(date: any) => {
            setFilters(prev => ({ ...prev, date }));
            setCurrentPage(1);
          }}
        />

        <Button
          onClick={handleDownload}
          children={d_loading ? 'Loading...' : 'Download'}
          border="#E2E4E9"
          bg="#0A3922"
          color="#fff"
        />
      </div>

      {/* ================= COUNT ================= */}
      {!getAllApplicantsQuery.isLoading && (
        <div style={{ margin: '10px 0', fontSize: '0.9rem', fontWeight: 500 }}>
          Showing {startRecord} – {endRecord} of {totalCount} records
        </div>
      )}

      {/* ================= TABLE ================= */}
      {getAllApplicantsQuery.isLoading ? (
        <CircularProgress sx={{ margin: '2rem auto', display: 'block' }} />
      ) : applicants.length ? (
        <>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Academic</th>
                <th>Academic Score</th>
                <th>HR Experience</th>
                <th>HR Experience Score</th>
                <th>Sector</th>
                <th>Trainings</th>
                <th>Trainings Score</th>
                <th>Present Employer</th>
                <th>Designation</th>
                <th>Designation Score</th>
                <th>Total Score</th>
                <th>Years of Experience</th>
                <th>Remarks</th>
              </tr>
            </thead>
            <tbody>
              {applicants.map((applicant: Applicants) => (
                <tr key={applicant.ApplicantId}>
                  <td>{applicant.Name || 'N/A'}</td>
                  <td>{applicant.Email || 'N/A'}</td>
                  <td>{applicant.Phone || 'N/A'}</td>
                  <td>{applicant.Academic || 'N/A'}</td>
                  <td>{applicant.Academic_Score || '0'}</td>
                  <td>{applicant.Hr_Experience || 'N/A'}</td>
                  <td>{applicant.Hr_Experience_Score || '0'}</td>
                  <td>{applicant.Sector || 'N/A'}</td>
                  <td>{applicant.Trainings || 'N/A'}</td>
                  <td>{applicant.Trainings_Score || '0'}</td>
                  <td>{applicant.Present_Employer || 'N/A'}</td>
                  <td>{applicant.Designation || 'N/A'}</td>
                  <td>{applicant.Designation_Score || '0'}</td>
                  <td>{applicant.Total_Score || '0'}</td>
                  <td>{applicant.Year_experience || 'N/A'}</td>
                  <td>{applicant.Remarks || 'N/A'}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* ================= PAGINATION ================= */}
          <div
            style={{
              marginTop: '20px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '12px',
              flexWrap: 'wrap',
            }}
          >
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(p => p - 1)}
              style={{
                padding: '8px 14px',
                borderRadius: '6px',
                border: '0px',
                backgroundColor: currentPage === 1 ? '#CBD5E0' : '#31ce94',
                color: '#084e2d',
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
              }}
            >
              ◀ Prev
            </button>

            <span style={{ fontWeight: 600 }}>
              Page {currentPage} of {pageCount}
            </span>

            <button
              disabled={currentPage === pageCount}
              onClick={() => setCurrentPage(p => p + 1)}
              style={{
                padding: '8px 14px',
                borderRadius: '6px',
                border: 'none',
                backgroundColor:
                  currentPage === pageCount ? '#CBD5E0' : '#38A169',
                color: '#083817',
                cursor:
                  currentPage === pageCount ? 'not-allowed' : 'pointer',
              }}
            >
              Next ▶
            </button>
          </div>
        </>
      ) : (
        <NoResult>No Results</NoResult>
      )}
    </div>
  );
};



export default DisplayApplicant;

const StyledError = styled('p')`
  color: red;
  font-size: 1.4rem;
  display: flex;
`;

const NoResult = styled('div')`
  margin-block-start: 0px;
`;
